import { useState, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  Image,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  SimpleGrid,
  useToast,
  Textarea,
  Switch,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
} from "react-icons/hi";
import Editor from "@monaco-editor/react";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";
import { useAuth } from "./AuthContext";
import { canEditRecord, withOwnerOrganisation } from "./utils/ownership";
import { unwrapApiResponse } from "./utils/unwrapApiResponse";
import { showApiErrorToast } from "./utils/showApiToast";

const CODIFY_BASE = "https://codify.poc.mrc.ai/api";
const MAPPING_MODES = ["set", "append", "merge"];
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
const schemaKey = (id, version) => (id ? `${id}|${version ?? ""}` : "");

const CodifySpecificationsEditor = ({ codifySpecification, isNew }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { get, post, put, getHeaders } = useAuthenticatedRequest();
  const { user } = useAuth();
  const isReadOnly = !isNew && !canEditRecord(user, codifySpecification);

  const [localSpecification, setLocalSpecification] = useState(codifySpecification || {});
  const [saving, setSaving] = useState(false);
  const [contractSchemas, setContractSchemas] = useState([]);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [headingTypes, setHeadingTypes] = useState([]);

  // A contract schema from the library generates the JSON Schema, so it can't be edited here
  const usesContractSchema = !!localSpecification.contractSchemaId;
  const mappings = localSpecification.mappings || [];

  useEffect(() => {
    get(`${CODIFY_BASE}/contractschemas`)
      .then((res) => setContractSchemas(asList(unwrapApiResponse(res.data))))
      .catch((err) => showApiErrorToast(toast, err, { title: "Couldn't load the contract schemas" }));
    // once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The heading types a mapping can be for: the definitions in the spec's collection
  useEffect(() => {
    const collection = localSpecification.codifyCollection;
    if (!collection) return setHeadingTypes([]);
    get(`${CODIFY_BASE}/definitions/headers?collection=${encodeURIComponent(collection)}`)
      .then((res) => setHeadingTypes([...new Set(asList(unwrapApiResponse(res.data)).map((d) => d.type).filter(Boolean))].sort()))
      .catch(() => setHeadingTypes([]));
    // get is recreated each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSpecification.codifyCollection]);

  const chooseContractSchema = async (key) => {
    if (!key) {
      setLocalSpecification((prev) => ({ ...prev, contractSchemaId: "", contractSchemaVersion: "" }));
      return;
    }
    const [id, version] = key.split("|");
    setLocalSpecification((prev) => ({ ...prev, contractSchemaId: id, contractSchemaVersion: version }));
    setLoadingSchema(true);
    try {
      const res = await get(`${CODIFY_BASE}/contractschemas/${encodeURIComponent(id)}/${encodeURIComponent(version)}`);
      const full = unwrapApiResponse(res.data);
      if (full?.jsonSchema) handleChange("schema", JSON.stringify(full.jsonSchema, null, 2));
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't load the contract schema" });
    } finally {
      setLoadingSchema(false);
    }
  };

  const setMappings = (next) => handleChange("mappings", next);
  const updateMapping = (index, field, value) => setMappings(mappings.map((m, i) => (i === index ? { ...m, [field]: value } : m)));

  useEffect(() => {
    if (codifySpecification) {
      setLocalSpecification(codifySpecification);
    }
  }, [codifySpecification]);

  const handleChange = (field, value) => {
    setLocalSpecification((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = isNew ? "POST" : "PUT";
      const url = isNew
        ? `https://codify.poc.mrc.ai/api/specifications`
        : `https://codify.poc.mrc.ai/api/specifications/${localSpecification._id}`;

      const payload = {
        ...localSpecification,
        contractSchemaId: localSpecification.contractSchemaId || null,
        contractSchemaVersion: localSpecification.contractSchemaId ? localSpecification.contractSchemaVersion : null,
        // Rows left completely empty aren't mappings
        mappings: mappings
          .filter((m) => m.type || m.path)
          .map((m) => ({ type: m.type || "", path: (m.path || "").trim(), mode: m.mode || "set" })),
        updatedDate: new Date().toISOString(),
      };

      const response = isNew
        ? await post(url, withOwnerOrganisation(payload, user))
        : await put(url, payload);

      const saved = unwrapApiResponse(response.data);
      setLocalSpecification(saved);

      toast({
        title: isNew ? "Specification created" : "Specification updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew) {
        navigate(`/codifyspecifications/${saved._id}`);
      }
    } catch (err) {
      // e.g. 400 CONTRACT_SCHEMA_NOT_FOUND or INVALID_MAPPING: the API's message says what's wrong
      showApiErrorToast(toast, err, { title: "Error saving specification" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate("/codify");
  };

  const handleValidateSchema = async () => {
    if (!localSpecification.schema || !localSpecification.schema.trim()) {
      toast({
        title: "No schema to validate",
        description: "Please enter a JSON Schema first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // First check if it's valid JSON
      const parsed = JSON.parse(localSpecification.schema);

      // Check if it has basic JSON Schema properties
      if (parsed.$schema || parsed.type || parsed.properties) {
        // Make a test validation call to the API to validate the schema structure
        const headers = getHeaders();
        const response = await fetch('https://codify.poc.mrc.ai/api/specifications/validate-schema', {
          method: 'POST',
          headers,
          body: localSpecification.schema,
        });

        if (response.ok) {
          toast({
            title: "Schema is valid",
            description: "The JSON Schema is correctly formatted",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } else {
          const error = await response.json();
          toast({
            title: "Invalid JSON Schema",
            description: error.message || "Schema validation failed",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: "Valid JSON, but not a JSON Schema",
          description: "The JSON is valid but doesn't appear to be a JSON Schema (missing $schema, type, or properties)",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Invalid JSON",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePrettifySchema = () => {
    if (!localSpecification.schema || !localSpecification.schema.trim()) {
      toast({
        title: "No schema to format",
        description: "Please enter JSON first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const parsed = JSON.parse(localSpecification.schema);
      const formatted = JSON.stringify(parsed, null, 4);
      handleChange("schema", formatted);
      
      toast({
        title: "Schema formatted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Cannot format",
        description: "Invalid JSON: " + err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            mb={4}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <HStack spacing={4} mb={4}>
              <Tooltip label="Back to Codify">
                <IconButton
                  icon={<HiOutlineArrowNarrowLeft />}
                  aria-label="Back"
                  onClick={handleClose}
                />
              </Tooltip>
              <Heading size="lg">
                {isNew ? "New Codify Specification" : "Edit Codify Specification"}
              </Heading>
              <Box flex="1" />
              <Button
                leftIcon={<HiOutlineSave />}
                colorScheme="linkedin"
                onClick={handleSave}
                isDisabled={isReadOnly}
                isLoading={saving}>
                Save
              </Button>
            </HStack>

            <Tabs colorScheme="linkedin">
              <TabList>
                <Tab>Details</Tab>
                <Tab>Schema</Tab>
                <Tab>Mappings{mappings.length ? ` · ${mappings.length}` : ""}</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={localSpecification.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Specification name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Input
                  value={localSpecification.status || ""}
                  onChange={(e) => handleChange("status", e.target.value)}
                  placeholder="e.g., draft, active, archived"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Collection</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY003"
                  value={
                    localSpecification.codifyCollection
                      ? { code: localSpecification.codifyCollection, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("codifyCollection", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Data Standard</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY001"
                  value={
                    localSpecification.dataStandard
                      ? { code: localSpecification.dataStandard, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("dataStandard", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Data Type</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY002"
                  value={
                    localSpecification.dataType
                      ? { code: localSpecification.dataType, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("dataType", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model Provider</FormLabel>
                <Input
                  value={localSpecification.modelProvider || ""}
                  onChange={(e) => handleChange("modelProvider", e.target.value)}
                  placeholder="e.g., OpenAI, Anthropic"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY006"
                  value={
                    localSpecification.model
                      ? { code: localSpecification.model, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("model", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Organisation Owner ID</FormLabel>
                <Input
                  value={localSpecification.organisationOwnerId || ""}
                  onChange={(e) => handleChange("organisationOwnerId", e.target.value)}
                  placeholder="Organisation owner ID"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Is Default</FormLabel>
                <Switch
                  isChecked={localSpecification.isDefault || false}
                  onChange={(e) => handleChange("isDefault", e.target.checked)}
                />
              </FormControl>
            </SimpleGrid>

                  <FormControl mt={4}>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={localSpecification.description || ""}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Description of this specification"
                      rows={4}
                    />
                  </FormControl>

                  {!isNew && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                <FormControl>
                  <FormLabel>Created By</FormLabel>
                  <Input value={localSpecification.createdBy || ""} isReadOnly />
                </FormControl>

                <FormControl>
                  <FormLabel>Created Date</FormLabel>
                  <Input
                    value={
                      localSpecification.createdDate
                        ? new Date(localSpecification.createdDate).toLocaleString()
                        : ""
                    }
                    isReadOnly
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Updated By</FormLabel>
                  <Input value={localSpecification.updatedBy || ""} isReadOnly />
                </FormControl>

                <FormControl>
                  <FormLabel>Updated Date</FormLabel>
                  <Input
                    value={
                      localSpecification.updatedDate
                        ? new Date(localSpecification.updatedDate).toLocaleString()
                        : ""
                    }
                    isReadOnly
                  />
                </FormControl>
              </SimpleGrid>
                  )}
                </TabPanel>

                <TabPanel>
                  <FormControl mb={3} maxW="520px">
                    <FormLabel>Contract schema</FormLabel>
                    <Select
                      bg="white"
                      value={schemaKey(localSpecification.contractSchemaId, localSpecification.contractSchemaVersion)}
                      onChange={(e) => chooseContractSchema(e.target.value)}
                      isDisabled={isReadOnly || loadingSchema}>
                      <option value="">None (custom schema)</option>
                      {contractSchemas.map((cs) => (
                        <option key={schemaKey(cs.id, cs.version)} value={schemaKey(cs.id, cs.version)}>
                          {cs.title || cs.id} · v{cs.version}
                        </option>
                      ))}
                      {/* A schema the list no longer has stays shown */}
                      {usesContractSchema &&
                        !contractSchemas.some((cs) => schemaKey(cs.id, cs.version) === schemaKey(localSpecification.contractSchemaId, localSpecification.contractSchemaVersion)) && (
                          <option value={schemaKey(localSpecification.contractSchemaId, localSpecification.contractSchemaVersion)}>
                            {localSpecification.contractSchemaId} · v{localSpecification.contractSchemaVersion}
                          </option>
                        )}
                    </Select>
                  </FormControl>
                  {usesContractSchema && (
                    <Alert status="info" mb={3} borderRadius="md" fontSize="sm">
                      <AlertIcon />
                      Generated from the contract schema library — the server refreshes it on save.
                    </Alert>
                  )}
                  <FormControl height="calc(100vh - 420px)" minH="320px">
                    <HStack mb={2}>
                      <FormLabel mb={0}>JSON Schema</FormLabel>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={handleValidateSchema}
                      >
                        Validate Schema
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="teal"
                        variant="outline"
                        onClick={handlePrettifySchema}
                        isDisabled={usesContractSchema}
                      >
                        Format JSON
                      </Button>
                    </HStack>
                    <Box 
                      border="1px solid" 
                      borderColor="gray.300" 
                      borderRadius="md" 
                      overflow="hidden"
                      height="calc(100% - 40px)"
                    >
                      <Editor
                        height="100%"
                        defaultLanguage="json"
                        value={localSpecification.schema || ""}
                        onChange={(value) => !usesContractSchema && handleChange("schema", value || "")}
                        theme="vs-light"
                        options={{
                          readOnly: usesContractSchema || isReadOnly,
                          minimap: { enabled: false },
                          fontSize: 13,
                          lineNumbers: "on",
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 4,
                          stickyScroll: { enabled: false },
                          folding: true,
                          showFoldingControls: "always",
                          foldingStrategy: "indentation",
                          foldingHighlight: true,
                          guides: {
                            indentation: true,
                            highlightActiveIndentation: true,
                          },
                        }}
                      />
                    </Box>
                  </FormControl>
                </TabPanel>

                <TabPanel>
                  <Text fontSize="sm" color="gray.600" mb={3} maxW="900px">
                    Where each heading type's output goes in the contract JSON. <code>name[]</code> means the list item for the
                    row's segment (matched by id). Types with no mapping go to a property named after the type.
                  </Text>
                  {!localSpecification.codifyCollection && (
                    <Alert status="warning" mb={3} borderRadius="md" fontSize="sm">
                      <AlertIcon />
                      Choose a collection on the Details tab to pick heading types.
                    </Alert>
                  )}
                  <Box borderWidth="1px" borderRadius="md" bg="white" overflowX="auto">
                    <Table size="sm">
                      <Thead>
                        <Tr><Th w="30%">Heading type</Th><Th>Path</Th><Th w="140px">Mode</Th><Th w="50px" /></Tr>
                      </Thead>
                      <Tbody>
                        {mappings.length === 0 && (
                          <Tr><Td colSpan={4} color="gray.500" fontSize="sm">No mappings yet.</Td></Tr>
                        )}
                        {mappings.map((m, i) => (
                          <Tr key={i}>
                            <Td>
                              <Select size="sm" value={m.type || ""} onChange={(e) => updateMapping(i, "type", e.target.value)} isDisabled={isReadOnly}>
                                <option value="">Choose…</option>
                                {[...new Set([...(m.type ? [m.type] : []), ...headingTypes])].map((t) => <option key={t} value={t}>{t}</option>)}
                              </Select>
                            </Td>
                            <Td>
                              <Input size="sm" fontFamily="mono" value={m.path || ""} placeholder="e.g. sections[].period"
                                onChange={(e) => updateMapping(i, "path", e.target.value)} isDisabled={isReadOnly} />
                            </Td>
                            <Td>
                              <Select size="sm" value={m.mode || "set"} onChange={(e) => updateMapping(i, "mode", e.target.value)} isDisabled={isReadOnly}>
                                {MAPPING_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                              </Select>
                            </Td>
                            <Td>
                              <IconButton size="sm" variant="ghost" colorScheme="red" aria-label="Delete mapping" icon={<HiOutlineTrash />}
                                onClick={() => setMappings(mappings.filter((_, j) => j !== i))} isDisabled={isReadOnly} />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  <Button mt={3} size="sm" leftIcon={<HiOutlinePlus />} isDisabled={isReadOnly}
                    onClick={() => setMappings([...mappings, { type: "", path: "", mode: "set" }])}>
                    Add mapping
                  </Button>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CodifySpecificationsEditor;
